import { GuestLinkDetailedResponseDto, GuestLinkListResponseDto, GuestLinksListQuery, GuestsListFilters, GuestsListQuery, GuestsListResponseDto, GuestWithLinksResponseDto, } from '@lucidlink/dto/web-service-api';
import { aesKeyToArray, arrayToBase64, decryptWithRsaBlockCipher, } from '@lucidlink/ts-crypto/crypto';
import { linearBackoffFullyJittered, retryUntilNoException, } from '@lucidlink/tscore';
import { parserFromDto } from '#fetcher/parse-utils';
import { prepareGuestLinkInvitation } from '#sdk/common/index';
import { Permission, } from '#types/sdk';
export const createGuestLinksManager = ({ filespaceFetcher, filespaceId, filePermissionsManager, getAdminCrypto, principalsCryptoRefresh, sendTelemetryEvent, workspaceId, }) => {
    return new GuestLinksManagerImpl(filespaceFetcher, getAdminCrypto, workspaceId, filespaceId, filePermissionsManager, principalsCryptoRefresh, sendTelemetryEvent);
};
class GuestLinksManagerImpl {
    filespaceFetcher;
    getAdminCrypto;
    workspaceId;
    filespaceId;
    filePermissionsManager;
    principalsCryptoRefresh;
    sendTelemetryEvent;
    constructor(filespaceFetcher, getAdminCrypto, workspaceId, filespaceId, filePermissionsManager, principalsCryptoRefresh, sendTelemetryEvent) {
        this.filespaceFetcher = filespaceFetcher;
        this.getAdminCrypto = getAdminCrypto;
        this.workspaceId = workspaceId;
        this.filespaceId = filespaceId;
        this.filePermissionsManager = filePermissionsManager;
        this.principalsCryptoRefresh = principalsCryptoRefresh;
        this.sendTelemetryEvent = sendTelemetryEvent;
    }
    async createGuestLink({ origin, ...params }) {
        const adminCrypto = await this.getAdminCrypto();
        const { invitation, invitationSecret } = await prepareGuestLinkInvitation({
            adminCrypto,
            workspaceId: this.workspaceId,
        });
        const { id } = await this.filespaceFetcher.post('guest-links', {
            body: { invitation, ...params },
            parser: parserFromDto(GuestLinkDetailedResponseDto),
        });
        const invSecretArray = await aesKeyToArray(invitationSecret);
        const linkSecret = arrayToBase64(invSecretArray, { urlSafe: true });
        await this.principalsCryptoRefresh();
        try {
            await this.filePermissionsManager.grantAccess(invitation.member.principalId, params.fsEntryId, [Permission.READ]);
        }
        catch (e) {
            void retryUntilNoException(() => this.deleteGuestLink(id), {
                retries: 3,
                sleep: linearBackoffFullyJittered({
                    delay: 500,
                    maxDelay: 5000,
                }),
            }).catch(() => { });
            throw e;
        }
        this.sendTelemetryEvent({
            name: 'GuestLinkCreatedFromOrigin',
            data: {
                workspaceId: this.workspaceId,
                filespaceId: this.filespaceId,
                customData: {
                    guestLinkId: id,
                    origin,
                },
            },
        });
        return {
            linkId: id,
            linkSecret,
        };
    }
    async listPaginatedGuestLinks(params = {}) {
        const { items: links, meta } = await this.filespaceFetcher.get('guest-links', {
            queryParams: GuestLinksListQuery.encode(params),
            parser: parserFromDto(GuestLinkListResponseDto),
        });
        const items = await Promise.all(links.map(this.remapLinkSecret));
        return { items, meta };
    }
    async getGuestLink(guestLinkId) {
        const link = await this.filespaceFetcher.get(['guest-links', guestLinkId], {
            parser: parserFromDto(GuestLinkDetailedResponseDto),
        });
        return this.remapLinkSecret(link);
    }
    async addEmailsToGuestLink({ guestLinkId, emails, }) {
        await this.filespaceFetcher.put(['guest-links', guestLinkId, 'acl'], {
            body: { emails },
        });
    }
    async updateGuestLink({ guestLinkId, name, expiresAt, }) {
        const link = await this.filespaceFetcher.patch(['guest-links', guestLinkId], {
            body: {
                ...(name !== undefined && { name }),
                ...(expiresAt !== undefined && { expiresAt }),
            },
            parser: parserFromDto(GuestLinkDetailedResponseDto),
        });
        return this.remapLinkSecret(link);
    }
    async deleteGuestLinkAcl({ email, guestLinkId }) {
        await this.filespaceFetcher.delete(['guest-links', guestLinkId, 'acl'], {
            queryParams: { email },
        });
    }
    async deleteGuestLink(guestLinkId) {
        await this.filespaceFetcher.delete(['guest-links', guestLinkId]);
    }
    listGuests(params = {}) {
        return this.filespaceFetcher.get(['guests'], {
            queryParams: GuestsListQuery.encode({
                ...params,
                filters: GuestsListFilters.encode(params.filters ?? {}),
            }),
            parser: parserFromDto(GuestsListResponseDto),
        });
    }
    getGuest(email) {
        return this.filespaceFetcher.get(['guests', encodeURIComponent(email)], {
            parser: parserFromDto(GuestWithLinksResponseDto),
        });
    }
    async removeGuestFromAllLinks(email) {
        await this.filespaceFetcher.delete([
            'guests',
            encodeURIComponent(email),
        ]);
    }
    remapLinkSecret = async ({ eInvitationSecretByAdminKey, ...link }) => {
        const adminCrypto = await this.getAdminCrypto();
        const adminKeys = await adminCrypto.verifyAndGetAdminKeys();
        const invitationSecretBuffer = await decryptWithRsaBlockCipher(eInvitationSecretByAdminKey, adminKeys.encryptPair.privateKey);
        const linkSecret = arrayToBase64(invitationSecretBuffer, {
            urlSafe: true,
        });
        return { ...link, linkSecret };
    };
}
